import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ArrowRight, BookOpen, Target, Users, Play, ClipboardList } from "lucide-react";
import { Reveal } from "./Reveal";
import SectionHeading from "./SectionHeading";
import { METODOLOGIA } from "../data/siteData";

const CORES = ["#8DC63F", "#3B82F6", "#F97316", "#A855F7"];
const PROG_ICONS = [BookOpen, Target, Users, Play];

const Metodologia = () => {
  const [ativa, setAtiva] = useState(0);
  const semana = METODOLOGIA.semanas[ativa];
  const cor = CORES[ativa];

  return (
    <section id="metodologia" data-testid="metodologia-section" className="relative bg-night py-20 md:py-28">
      <div className="turf-lines pointer-events-none absolute inset-0" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow={`Metodologia de treinamento — ${METODOLOGIA.categorias}`}
          title="Planejamento Mensal"
          lead={METODOLOGIA.objetivoGeral}
        />

        <Reveal>
          <div className="mb-10 grid grid-cols-2 gap-3 md:grid-cols-4" role="tablist" aria-label="Semanas do planejamento mensal">
            {METODOLOGIA.semanas.map((s, i) => (
              <button
                key={s.numero}
                role="tab"
                aria-selected={ativa === i}
                data-testid={`metodologia-tab-${s.numero}`}
                onClick={() => setAtiva(i)}
                className={`rounded-2xl border p-4 text-left transition-all duration-300 ${
                  ativa === i
                    ? "text-night"
                    : "border-white/10 bg-navy/60 text-white hover:border-white/30"
                }`}
                style={ativa === i ? { backgroundColor: CORES[i], borderColor: CORES[i] } : {}}
              >
                <span className="block text-[11px] font-bold uppercase tracking-[0.2em] opacity-80">
                  {s.numero}ª Semana
                </span>
                <span className="block font-display text-2xl uppercase tracking-wide">{s.tema}</span>
              </button>
            ))}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={ativa}
            data-testid="metodologia-panel"
            role="tabpanel"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="grid gap-8 lg:grid-cols-3"
          >
            <div className="space-y-3 lg:col-span-2">
              {semana.conteudos.map((c) => (
                <div
                  key={c.cat}
                  data-testid={`metodologia-row-${c.cat.toLowerCase()}`}
                  className="flex items-center gap-4 rounded-xl border border-white/10 bg-navy/60 p-4"
                >
                  <span
                    className="min-w-[74px] shrink-0 whitespace-nowrap rounded-full px-3 py-1 text-center text-xs font-bold"
                    style={{ backgroundColor: `${cor}22`, color: cor }}
                  >
                    {c.cat}
                  </span>
                  <p className="text-sm leading-relaxed text-white/80">{c.texto}</p>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border p-6" style={{ borderColor: `${cor}55`, backgroundColor: `${cor}0d` }}>
              <h3 className="mb-4 font-display text-2xl uppercase tracking-wide" style={{ color: cor }}>
                Objetivos da semana
              </h3>
              <ul className="space-y-3">
                {semana.objetivos.map((o) => (
                  <li key={o} className="flex gap-2.5 text-sm text-white/80">
                    <Check size={17} style={{ color: cor }} className="mt-0.5 shrink-0" strokeWidth={3} />
                    {o}
                  </li>
                ))}
              </ul>
              {semana.organizacao && (
                <div className="mt-6 border-t border-white/10 pt-5">
                  <h4 className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-white/60">
                    Organização da semana
                  </h4>
                  <ul className="space-y-2">
                    {semana.organizacao.map((o) => (
                      <li key={o} className="flex gap-2 text-xs text-white/70">
                        <ClipboardList size={14} style={{ color: cor }} className="mt-0.5 shrink-0" />
                        {o}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        <Reveal className="mt-16">
          <div className="rounded-2xl border border-white/10 bg-navy/60 p-6 md:p-8">
            <h3 className="mb-8 text-center font-display text-2xl uppercase tracking-wide text-gold">
              Progressão pedagógica
            </h3>
            <div className="grid gap-6 sm:grid-cols-4">
              {METODOLOGIA.progressao.map((p, i) => {
                const Icon = PROG_ICONS[i];
                return (
                  <div key={p.titulo} className="relative text-center" data-testid={`progressao-${i + 1}`}>
                    <span
                      className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full"
                      style={{ backgroundColor: `${CORES[i]}22`, color: CORES[i] }}
                    >
                      <Icon size={22} />
                    </span>
                    <p className="text-xs font-bold uppercase tracking-widest" style={{ color: CORES[i] }}>
                      {p.titulo}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-white/65">{p.texto}</p>
                    {i < 3 && (
                      <ArrowRight size={18} className="absolute -right-3 top-4 hidden text-white/30 sm:block" />
                    )}
                  </div>
                );
              })}
            </div>
            <p className="mt-10 text-center font-display text-3xl uppercase tracking-widest text-white sm:text-4xl">
              {METODOLOGIA.lema}
            </p>
            <p className="mt-2 text-center text-sm font-semibold uppercase tracking-wider text-lawn">
              {METODOLOGIA.sublema}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Metodologia;
